import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Star, Maximize2, X, ExternalLink, Camera, Plus } from 'lucide-react';
import { ReviewScreenshot } from '../types/siteContent';

interface GoogleReviewsCarouselProps {
  screenshots?: ReviewScreenshot[];
  title?: string;
  subtitle?: string;
  googleReviewsUrl?: string;
  rating?: number;
  ratingText?: string;
  onOpenAdminReviews?: () => void;
  isAdmin?: boolean;
}

export default function GoogleReviewsCarousel({
  screenshots = [],
  title = 'Какво споделят клиентите за нас',
  subtitle = 'Скрийншоти и оценки от доволни купувачи, продавачи и наематели във Варна',
  googleReviewsUrl,
  rating = 5.0,
  ratingText,
  onOpenAdminReviews,
  isAdmin = false
}: GoogleReviewsCarouselProps) {
  const items = Array.isArray(screenshots) ? screenshots.filter(s => s && s.imageUrl) : [];
  const totalItems = items.length;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef<any>(null);

  const effectiveRating = typeof rating === 'number' && !isNaN(rating) 
    ? Math.min(5, Math.max(1, rating)) 
    : 5.0;
  const ratingFormatted = effectiveRating.toFixed(1);
  const displayRatingText = ratingText && ratingText.trim().length > 0 
    ? ratingText 
    : `${ratingFormatted} Рейтинг в Google`;

  // Keep index in valid range
  useEffect(() => {
    if (currentIndex >= totalItems && totalItems > 0) {
      setCurrentIndex(0);
    }
  }, [totalItems, currentIndex]);

  // Auto-rotation every 5 seconds (paused on hover or when lightbox is open)
  useEffect(() => {
    if (isPaused || lightboxImage || totalItems <= 1) return;

    timerRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % totalItems);
    }, 5000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isPaused, lightboxImage, totalItems]);

  const handlePrev = () => {
    if (totalItems <= 1) return;
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : totalItems - 1));
  };

  const handleNext = () => {
    if (totalItems <= 1) return;
    setCurrentIndex((prev) => (prev + 1) % totalItems);
  };

  const effectiveGoogleUrl = googleReviewsUrl && googleReviewsUrl.trim().length > 0 
    ? googleReviewsUrl.trim() 
    : 'https://maps.google.com/';

  return (
    <div className="w-full py-16 md:py-20 bg-slate-900 text-white rounded-3xl my-16 px-4 sm:px-6 md:px-10 relative overflow-hidden shadow-2xl border border-slate-800">
      {/* Decorative ambient gradients */}
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-[#C5A46E]/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-10 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-10">
          <h3 className="text-2xl sm:text-3xl md:text-4xl font-black text-white uppercase tracking-tight">
            {title}
          </h3>
          <p className="text-xs sm:text-sm text-slate-400 mt-2 font-light max-w-xl mx-auto leading-relaxed">
            {subtitle}
          </p>

          {/* Rating stars & "Виж в Google" Button Group */}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <div className="inline-flex items-center gap-2.5 bg-slate-950/70 border border-slate-800 px-4 py-2.5 rounded-2xl shadow-lg">
              <div className="flex items-center gap-1">
                {[0, 1, 2, 3, 4].map((starIdx) => {
                  const fillPct = Math.max(0, Math.min(100, Math.round((effectiveRating - starIdx) * 100)));
                  return (
                    <div key={starIdx} className="relative w-4 h-4 shrink-0">
                      {/* Background / outline empty star */}
                      <Star className="w-4 h-4 text-slate-600 fill-slate-800/80 stroke-slate-500" />
                      {/* Filled star with precise percentage clipping */}
                      {fillPct > 0 && (
                        <div 
                          className="absolute inset-0 overflow-hidden"
                          style={{ width: `${fillPct}%` }}
                        >
                          <Star className="w-4 h-4 text-amber-400 fill-amber-400 stroke-amber-400 shrink-0" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <span className="text-xs font-bold text-white tracking-wide">{displayRatingText}</span>
            </div>

            {/* "Виж в Google" Button */}
            {effectiveGoogleUrl && (
              <a
                href={effectiveGoogleUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2.5 px-5 py-2.5 bg-white hover:bg-slate-100 text-[#0F1E32] rounded-2xl text-xs font-bold uppercase tracking-wider transition-all duration-200 shadow-xl hover:scale-105 active:scale-95 border border-white cursor-pointer group"
                title="Отвори страницата с отзиви в Google"
              >
                <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
                </svg>
                <span>Виж в Google</span>
                <ExternalLink className="w-3.5 h-3.5 text-slate-500 group-hover:text-[#0F1E32] transition-colors" />
              </a>
            )}
          </div>
        </div>

        {/* Carousel or Empty State */}
        {totalItems > 0 ? (
          <div 
            className="relative max-w-3xl mx-auto"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            {/* Main Screenshot Slide Viewport */}
            <div className="overflow-hidden rounded-2xl bg-slate-950 border border-slate-800 shadow-2xl relative min-h-[280px] flex items-center justify-center p-3 sm:p-6 md:p-8">
              {(() => {
                const item = items[currentIndex];
                return (
                  <div key={item.id || currentIndex} className="w-full flex flex-col items-center animate-fade-in">
                    <div 
                      onClick={() => setLightboxImage(item.imageUrl)}
                      className="group/img relative max-h-[500px] w-full flex items-center justify-center rounded-xl overflow-hidden bg-slate-900 border border-slate-800/80 cursor-zoom-in"
                      title="Кликнете за преглед на цял екран"
                    >
                      <img
                        src={item.imageUrl}
                        alt={item.caption || 'Google отзив'}
                        className="max-h-[480px] w-auto max-w-full object-contain rounded-lg transition-transform duration-300 group-hover/img:scale-[1.02]"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800&fit=crop&q=60';
                        }}
                      />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                        <span className="px-3.5 py-1.5 rounded-full bg-black/85 backdrop-blur-md text-white text-xs font-semibold flex items-center gap-1.5 shadow-xl border border-white/20">
                          <Maximize2 className="w-3.5 h-3.5 text-[#C5A46E]" />
                          Преглед на цял екран
                        </span>
                      </div>
                    </div>
                    {item.caption && (
                      <p className="text-xs sm:text-sm text-slate-300 mt-4 text-center italic font-light max-w-lg">
                        „{item.caption}“
                      </p>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Left / Right Chevron Navigation Buttons */}
            {totalItems > 1 && (
              <>
                <button
                  type="button"
                  onClick={handlePrev}
                  aria-label="Предишен отзив"
                  className="absolute left-2 sm:-left-5 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-slate-800/90 hover:bg-[#C5A46E] text-white hover:text-slate-950 flex items-center justify-center transition-all duration-200 shadow-2xl border border-slate-700 cursor-pointer active:scale-95"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  type="button"
                  onClick={handleNext}
                  aria-label="Следващ отзив"
                  className="absolute right-2 sm:-right-5 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-slate-800/90 hover:bg-[#C5A46E] text-white hover:text-slate-950 flex items-center justify-center transition-all duration-200 shadow-2xl border border-slate-700 cursor-pointer active:scale-95"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </>
            )}

            {/* Dots Indicator & Slide Counter */}
            {totalItems > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6">
                {Array.from({ length: totalItems }).map((_, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setCurrentIndex(idx)}
                    aria-label={`Отзив ${idx + 1}`}
                    className={`transition-all duration-300 rounded-full cursor-pointer ${
                      idx === currentIndex 
                        ? 'w-7 h-2 bg-[#C5A46E]' 
                        : 'w-2 h-2 bg-slate-700 hover:bg-slate-500'
                    }`}
                  />
                ))}
                <span className="text-[11px] text-slate-400 font-mono ml-2">
                  {currentIndex + 1} / {totalItems}
                </span>
              </div>
            )}

            {/* Thumbnail Preview Strip if multiple screenshots exist */}
            {totalItems > 1 && (
              <div className="max-w-2xl mx-auto mt-6 flex items-center justify-center gap-3 overflow-x-auto py-2 scrollbar-thin">
                {items.map((item, idx) => (
                  <button
                    key={item.id || idx}
                    type="button"
                    onClick={() => setCurrentIndex(idx)}
                    className={`relative shrink-0 w-16 h-12 rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                      idx === currentIndex 
                        ? 'border-[#C5A46E] scale-105 shadow-md ring-2 ring-[#C5A46E]/50' 
                        : 'border-slate-800 opacity-50 hover:opacity-100 hover:border-slate-600'
                    }`}
                  >
                    <img
                      src={item.imageUrl}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* Empty State when no screenshots are uploaded yet */
          <div className="max-w-xl mx-auto text-center p-8 sm:p-10 rounded-2xl bg-slate-950/70 border border-slate-800">
            <Camera className="w-10 h-10 text-[#C5A46E] mx-auto mb-3 opacity-80" />
            <h4 className="text-base font-bold text-white mb-2">
              Секцията за Google отзиви е готова
            </h4>
            <p className="text-xs text-slate-400 font-light leading-relaxed mb-6">
              Скрийншотите от реални Google отзиви ще се показват тук веднага след като ги качите в административния панел.
            </p>

            {isAdmin && onOpenAdminReviews && (
              <button
                type="button"
                onClick={onOpenAdminReviews}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#C5A46E] hover:bg-[#b08e58] text-[#0F1E32] rounded-xl text-xs font-bold uppercase tracking-wider transition shadow-lg cursor-pointer"
              >
                <Plus className="w-4 h-4" />
                <span>Качи първия скрийншот от отзив</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Lightbox Modal for Fullscreen Screenshot Zoom */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 z-70 bg-black/95 backdrop-blur-md flex flex-col items-center justify-center p-4 select-none animate-fade-in"
          onClick={() => setLightboxImage(null)}
        >
          <button
            type="button"
            onClick={() => setLightboxImage(null)}
            className="absolute top-5 right-5 p-2.5 rounded-full bg-white/10 hover:bg-white text-white hover:text-slate-950 transition cursor-pointer"
            aria-label="Затвори преглед"
          >
            <X className="w-6 h-6" />
          </button>
          <div 
            className="max-w-4xl max-h-[85vh] p-2 flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightboxImage}
              alt="Скрийншот на отзив в пълен размер"
              className="max-h-[82vh] max-w-[90vw] object-contain rounded-xl shadow-2xl border border-white/10"
            />
          </div>
          <p className="text-xs text-slate-400 mt-3 font-light">
            Кликнете извън снимката или натиснете бутона за затваряне
          </p>
        </div>
      )}
    </div>
  );
}
